import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { mergeAttributes, Node, type CommandProps, type JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ShortAnswerAnswerNodeView from './short-answer-answer-node-view';
import { findShortAnswerAnswerChild } from './short-answer-answer-sibling';
import ShortAnswerAnswerWrapperNodeView from './short-answer-answer-wrapper-node-view';
import ShortAnswerExplanationNodeView from './short-answer-explanation-node-view';
import ShortAnswerHintNodeView from './short-answer-hint-node-view';
import ShortAnswerNodeView from './short-answer-node-view';
import ShortAnswerQuestionNodeView from './short-answer-question-node-view';
import { applyToolLabelToAttrs, DEFAULT_SHORT_ANSWER_TOOL_LABEL, parseAssessmentOptionsJson } from './tool-settings';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        shortAnswer: {
            insertShortAnswer: (options?: InsertShortAnswerOptions) => ReturnType;
            insertBareShortAnswer: (options?: InsertShortAnswerOptions) => ReturnType;
            insertShortAnswerAnswerWrapper: (shortAnswerNode: PMNode) => ReturnType;
            applyShortAnswerTool: (shortAnswerPos: number, toolLabel: string) => ReturnType;
        };
    }
}

export interface InsertShortAnswerOptions {
    instruction?: string;
    question?: string;
    hint?: string;
    explanation?: string;
    title?: string;
    placeholder?: string;
    correctAnswers?: string[];
}

export interface ShortAnswerNodeAttrs {
    id: string | null;
    title: string;
    assessmentType: string;
    assessmentOptions: Record<string, unknown>;
    toolLabel: string;
    answers: string[];
    preferredIndex: number | null;
}

const defaultShortAnswerNode = (options: InsertShortAnswerOptions) => {
    const shortAnswerQuestionNode: JSONContent = {
        type: 'shortAnswerQuestion',
        content: [
            {
                type: 'paragraph',
                content: options.question ? [{ type: 'text', text: options.question }] : undefined,
            },
        ],
    };

    const shortAnswerAnswerNode: JSONContent = {
        type: 'shortAnswerAnswer',
        attrs: {
            answers: options.correctAnswers ? options.correctAnswers : [''],
            ...applyToolLabelToAttrs(DEFAULT_SHORT_ANSWER_TOOL_LABEL, {
                answers: options.correctAnswers ? options.correctAnswers : [''],
                preferredIndex: null,
            }),
        },
    };

    const shortAnswerHintNode: JSONContent = {
        type: 'shortAnswerHint',
        content: [
            {
                type: 'paragraph',
                content: options.hint ? [{ type: 'text', text: options.hint }] : undefined,
            },
        ],
    };

    const shortAnswerExplanationNode: JSONContent = {
        type: 'shortAnswerExplanation',
        content: [
            {
                type: 'paragraph',
                content: options.explanation ? [{ type: 'text', text: options.explanation }] : undefined,
            },
        ],
    };

    return {
        type: 'shortAnswer',
        attrs: {
            title: options.title || 'Short Answer Question',
        },
        content: [shortAnswerQuestionNode, shortAnswerAnswerNode, shortAnswerHintNode, shortAnswerExplanationNode],
    };
};

// Short Answer Node Extension
export const ShortAnswerNodeHeadless = Node.create({
    name: 'shortAnswer',

    group: 'block',

    content: 'shortAnswerQuestion shortAnswerAnswer shortAnswerAnswerWrapper? shortAnswerHint shortAnswerExplanation',

    defining: true,

    isolating: true,

    selectable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-sa-id'),
                renderHTML: attributes => ({
                    'data-sa-id': attributes.id,
                }),
            },
            title: {
                default: 'Short Answer Question',
                parseHTML: element => element.getAttribute('data-title') || 'Short Answer Question',
                renderHTML: attributes => ({
                    'data-title': attributes.title,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer',
            }),
            0,
        ];
    },

    addCommands() {
        return {
            insertShortAnswer:
                (options: InsertShortAnswerOptions = {}) =>
                ({ commands }) => {
                    const shortAnswerNode = defaultShortAnswerNode(options);
                    const instructionNode: JSONContent = {
                        type: 'paragraph',
                        content: options.instruction ? [{ type: 'text', text: options.instruction }] : undefined,
                        attrs: {
                            textAlign: null,
                        },
                    };

                    const questionSet = {
                        type: 'questionSet',
                        attrs: {
                            activityType: 'participation',
                            questionType: 'shortAnswer',
                        },
                        content: [instructionNode, shortAnswerNode],
                    };
                    return commands.insertContent(questionSet);
                },
            insertBareShortAnswer:
                (options: InsertShortAnswerOptions = {}) =>
                ({ commands }) => {
                    const shortAnswerNode = defaultShortAnswerNode(options);
                    return commands.insertContent(shortAnswerNode);
                },
            insertShortAnswerAnswerWrapper:
                (shortAnswerNode: PMNode) =>
                ({ commands, state, chain }) => {
                    let shortAnswerNodePos = -1;
                    state.doc.descendants((node, pos) => {
                        if (node === shortAnswerNode) {
                            shortAnswerNodePos = pos;
                            return false;
                        }
                    });

                    if (shortAnswerNodePos === -1) {
                        console.error('No shortAnswer node found.');
                        return commands.focus();
                    }

                    let insertPos = shortAnswerNodePos + 1;
                    let commandChain = chain();
                    shortAnswerNode.content.forEach(child => {
                        insertPos += child.nodeSize;
                        if (child.type.name === 'shortAnswerAnswer') {
                            commandChain = commandChain.insertContentAt(insertPos, {
                                type: 'shortAnswerAnswerWrapper',
                                attrs: {
                                    content: 'This is your {{ANSWER}}',
                                    language: 'plaintext',
                                },
                            });
                        }
                        return;
                    });
                    return commandChain.run();
                },
            applyShortAnswerTool:
                (shortAnswerPos: number, toolLabel: string) =>
                ({ state, dispatch }: CommandProps) => {
                    const hit = findShortAnswerAnswerChild(state.doc, shortAnswerPos);
                    if (!hit) {
                        return false;
                    }
                    const nextAttrs = applyToolLabelToAttrs(toolLabel, hit.node.attrs as Record<string, unknown>);
                    const tr = state.tr.setNodeMarkup(hit.pos, undefined, nextAttrs);
                    dispatch?.(tr);
                    return true;
                },
        };
    },
});

// Short Answer Question Node Extension
export const ShortAnswerQuestionNodeHeadless = Node.create({
    name: 'shortAnswerQuestion',

    group: 'shortAnswerContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-question-id'),
                renderHTML: attributes => ({
                    'data-question-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer-question"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer-question',
            }),
            0,
        ];
    },
});

// Short Answer Answer Node Extension
export const ShortAnswerAnswerNodeHeadless = Node.create({
    name: 'shortAnswerAnswer',

    group: 'shortAnswerContent',

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-answer-id'),
                renderHTML: attributes => ({
                    'data-answer-id': attributes.id,
                }),
            },
            toolLabel: {
                default: DEFAULT_SHORT_ANSWER_TOOL_LABEL,
                parseHTML: element => element.getAttribute('data-tool-label') || DEFAULT_SHORT_ANSWER_TOOL_LABEL,
                renderHTML: attributes => ({
                    'data-tool-label': attributes.toolLabel ?? DEFAULT_SHORT_ANSWER_TOOL_LABEL,
                }),
            },
            answers: {
                default: [],
                parseHTML: element => {
                    const answersAttr = element.getAttribute('data-answers');
                    if (answersAttr) {
                        try {
                            return JSON.parse(answersAttr);
                        } catch {
                            return [];
                        }
                    }
                    return [];
                },
                renderHTML: attributes => ({
                    'data-answers': JSON.stringify(attributes.answers || []),
                }),
            },
            preferredIndex: {
                default: null,
                parseHTML: element => {
                    const raw = element.getAttribute('data-preferred-index');
                    if (raw == null || raw === '') {
                        return null;
                    }
                    const n = Number.parseInt(raw, 10);
                    return Number.isNaN(n) ? null : n;
                },
                renderHTML: attributes => {
                    if (attributes.preferredIndex != null && typeof attributes.preferredIndex === 'number') {
                        return { 'data-preferred-index': String(attributes.preferredIndex) };
                    }
                    return {};
                },
            },
            assessmentType: {
                default: 'string',
                parseHTML: element => element.getAttribute('data-assessment-type') || 'string',
                renderHTML: attributes => ({
                    'data-assessment-type': attributes.assessmentType ?? 'string',
                }),
            },
            assessmentOptions: {
                default: () => ({}),
                parseHTML: element => parseAssessmentOptionsFromElement(element as HTMLElement),
                renderHTML: attributes => {
                    const opts = attributes.assessmentOptions;
                    if (!opts || typeof opts !== 'object' || Array.isArray(opts) || Object.keys(opts as object).length === 0) {
                        return {};
                    }
                    return {
                        'data-assessment-options': encodeURIComponent(JSON.stringify(opts)),
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer-answer"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer-answer',
            }),
        ];
    },
});

// Short Answer Hint Node Extension
export const ShortAnswerHintNodeHeadless = Node.create({
    name: 'shortAnswerHint',

    group: 'shortAnswerContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-hint-id'),
                renderHTML: attributes => ({
                    'data-hint-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer-hint"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer-hint',
            }),
            0,
        ];
    },
});

// Short Answer Answer Wrapper Node Extension
export const ShortAnswerAnswerWrapperNodeHeadless = Node.create({
    name: 'shortAnswerAnswerWrapper',

    group: 'shortAnswerContent',

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-answer-wrapper-id'),
                renderHTML: attributes => ({
                    'data-answer-wrapper-id': attributes.id,
                }),
            },
            content: {
                default: '{{ANSWER}}',
                parseHTML: element => element.getAttribute('data-content') || '{{ANSWER}}',
                renderHTML: attributes => ({
                    'data-content': attributes.content,
                }),
            },
            language: {
                default: 'plaintext',
                parseHTML: element => element.getAttribute('data-language') || 'plaintext',
                renderHTML: attributes => ({
                    'data-language': attributes.language,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer-answer-wrapper"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer-answer-wrapper',
            }),
        ];
    },
});

// Short Answer Explanation Node Extension
export const ShortAnswerExplanationNodeHeadless = Node.create({
    name: 'shortAnswerExplanation',

    group: 'shortAnswerContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-explanation-id'),
                renderHTML: attributes => ({
                    'data-explanation-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="short-answer-explanation"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'short-answer-explanation',
            }),
            0,
        ];
    },
});

function parseAssessmentOptionsFromElement(element: HTMLElement): Record<string, unknown> {
    const raw = element.getAttribute('data-assessment-options');
    if (raw == null || raw === '') {
        return {};
    }
    try {
        return parseAssessmentOptionsJson(decodeURIComponent(raw));
    } catch {
        return {};
    }
}

export const ShortAnswerExplanationNode = ShortAnswerExplanationNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerExplanationNodeView);
    },
});

export const ShortAnswerAnswerWrapperNode = ShortAnswerAnswerWrapperNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerAnswerWrapperNodeView);
    },
});

export const ShortAnswerHintNode = ShortAnswerHintNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerHintNodeView);
    },
});

export const ShortAnswerAnswerNode = ShortAnswerAnswerNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerAnswerNodeView);
    },
});

export const ShortAnswerQuestionNode = ShortAnswerQuestionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerQuestionNodeView);
    },
});

export const ShortAnswerNode = ShortAnswerNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ShortAnswerNodeView, { trackNodeViewPosition: true });
    },
});
